import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, CheckCircle } from "@phosphor-icons/react";
import { useAuth } from "../lib/auth";
import api, { formatApiError, errContext } from "../lib/api";

export default function RegisterPage() {
  const { refreshMe } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get("invite");

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [orgName, setOrgName] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [inviteInfo, setInviteInfo] = useState(null);

  useEffect(() => {
    if (inviteToken) setInviteInfo({ token: inviteToken });
  }, [inviteToken]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      if (inviteToken) {
        // Invite flow: original register endpoint (auto-creates account under existing tenant)
        await api.post("/auth/register", {
          email, password, name, invite_token: inviteToken,
        });
        await refreshMe();
        toast.success("Welcome to the team!");
        navigate("/");
      } else {
        // Public request flow: NO account is created yet — awaits owner approval
        await api.post("/public/access-request", {
          email, name, org_name: orgName, reason: reason || undefined,
        });
        setSubmitted(true);
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail, errContext(err)) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50 bg-grid px-4 py-8">
        <div className="w-full max-w-md bg-white border border-zinc-950 p-8" data-testid="request-submitted">
          <div className="w-14 h-14 bg-emerald-100 border border-emerald-600 flex items-center justify-center mb-4">
            <CheckCircle size={28} weight="fill" className="text-emerald-700" />
          </div>
          <div className="font-mono text-[10px] tracking-[0.2em] text-zinc-500 mb-2">/ REQUEST SUBMITTED</div>
          <h2 className="font-display text-3xl font-black tracking-tight">Thanks — we got it.</h2>
          <p className="text-sm text-zinc-600 mt-3">
            Your request to access SmartERP has been sent to our team for review. We'll email <strong>{email}</strong> as soon as it's decided — usually within one business day.
          </p>
          <div className="mt-8 pt-6 border-t border-zinc-200 text-xs text-zinc-500">
            Already have an account?{" "}
            <Link to="/login" className="text-zinc-950 underline underline-offset-4 hover:text-[#FF3E00]">Sign in</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 bg-grid px-4 py-8">
      <div className="w-full max-w-md bg-white border border-zinc-950 p-8">
        <div className="font-mono text-[10px] tracking-[0.2em] text-zinc-500 mb-2">
          / {inviteToken ? "ACCEPT_INVITATION" : "REQUEST_ACCESS"}
        </div>
        <h2 className="font-display text-3xl font-black tracking-tight">{inviteToken ? "Join your team." : "Request access."}</h2>
        <p className="text-sm text-zinc-500 mt-1">
          {inviteToken
            ? "You've been invited to a SmartERP organization."
            : "Tell us about your organization and we'll review your request within one business day."}
        </p>

        <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="register-form">
          <div>
            <label className="label-sm text-zinc-500">Full name</label>
            <input
              data-testid="register-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              minLength={2}
              className="w-full border-b border-zinc-900/20 focus:border-zinc-950 bg-transparent outline-none py-2 text-sm"
            />
          </div>
          <div>
            <label className="label-sm text-zinc-500">Work email</label>
            <input
              data-testid="register-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full border-b border-zinc-900/20 focus:border-zinc-950 bg-transparent outline-none py-2 text-sm"
            />
          </div>
          {inviteToken && (
            <div>
              <label className="label-sm text-zinc-500">Password (min 8)</label>
              <input
                data-testid="register-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                className="w-full border-b border-zinc-900/20 focus:border-zinc-950 bg-transparent outline-none py-2 text-sm"
              />
            </div>
          )}
          {!inviteToken && (
            <>
              <div>
                <label className="label-sm text-zinc-500">Organization name</label>
                <input
                  data-testid="register-org-input"
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  placeholder="Acme Corp"
                  required
                  minLength={2}
                  className="w-full border-b border-zinc-900/20 focus:border-zinc-950 bg-transparent outline-none py-2 text-sm"
                />
              </div>
              <div>
                <label className="label-sm text-zinc-500">Why do you want to try SmartERP? (optional)</label>
                <textarea
                  data-testid="register-reason-input"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="What you're hoping to solve, how many people would use it, etc."
                  rows={3}
                  className="w-full mt-1 border border-zinc-200 focus:border-zinc-950 bg-transparent outline-none py-2 px-3 text-sm resize-none"
                />
              </div>
            </>
          )}
          {inviteInfo && (
            <div className="border border-emerald-300 bg-emerald-50 text-emerald-900 text-xs px-3 py-2 font-mono" data-testid="invite-banner">
              Joining via invitation · token {inviteInfo.token.slice(0, 10)}…
            </div>
          )}

          {error && (
            <div className="border border-[#D0021B] bg-red-50 text-[#D0021B] text-xs px-3 py-2 font-mono" data-testid="register-error">
              {error}
            </div>
          )}

          <button
            data-testid="register-submit-btn"
            type="submit"
            disabled={loading}
            className="w-full bg-zinc-950 text-white font-semibold text-sm py-3 hover:bg-[#FF3E00] disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
          >
            {loading ? "SUBMITTING…" : (inviteToken ? "JOIN ORGANIZATION" : "REQUEST ACCESS")}
            {!loading && <ArrowRight size={14} weight="bold" />}
          </button>

          <div className="text-xs text-zinc-500 pt-2">
            Have an account?{" "}
            <Link to="/login" data-testid="link-login" className="text-zinc-950 underline underline-offset-4 hover:text-[#FF3E00]">
              Sign in
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
